import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createReward, updateReward } from "../services/loyaltyService";
import RewardProductPicker, { toPickedProduct } from "./RewardProductPicker";
import { labelFor } from "../services/enumLabels";

/** The four reward types the desk hands out ("other" stays API-only). */
export const REWARD_TYPES = ["discount_amount", "discount_percent", "free_service", "free_product"];

const BLANK = {
  name: { en: "", ar: "" },
  description: { en: "", ar: "" },
  type: "discount_amount",
  value: "",
  product: null,
  points_required: "",
  stamps_required: "",
  validity_days: "",
  redemption_limit_per_client: "",
  total_redemption_limit: "",
  is_active: true,
  branches: [],
  services: [],
};

const toForm = (reward) =>
  reward
    ? {
        name: reward.translations?.name ?? { en: reward.name ?? "", ar: "" },
        description: reward.translations?.description ?? { en: "", ar: "" },
        type: reward.type,
        value: reward.value ?? "",
        product: toPickedProduct(reward.product),
        points_required: reward.points_required ? String(reward.points_required) : "",
        stamps_required: reward.stamps_required ? String(reward.stamps_required) : "",
        validity_days: reward.validity_days ?? "",
        redemption_limit_per_client: reward.redemption_limit_per_client ?? "",
        total_redemption_limit: reward.total_redemption_limit ?? "",
        is_active: Boolean(reward.is_active),
        branches: (reward.branches ?? []).map((b) => b.id),
        services: (reward.services ?? []).map((s) => s.id),
      }
    : BLANK;

const intOrNull = (v) => (v === "" || v == null ? null : Number(v));

const TWO_DECIMALS = /^\d+(\.\d{1,2})?$/;
const WHOLE = /^\d+$/;

/**
 * The server's rules, checked before sending so the form can say so at once.
 * The server stays the judge: its 422s are shown under the same fields.
 * - percent: required, > 0 and <= 100, up to 2 decimals;
 * - amount: required, > 0, up to 2 decimals;
 * - points / stamps: whole numbers >= 0, and at least one above 0.
 */
const clientErrors = (form, t) => {
  const found = {};
  const value = String(form.value ?? "").trim();

  if (form.type === "discount_percent" || form.type === "discount_amount") {
    const percent = form.type === "discount_percent";
    if (value === "") {
      found.value = [t(percent ? "loyalty.rewards.errors.percentRequired" : "loyalty.rewards.errors.amountRequired")];
    } else if (!TWO_DECIMALS.test(value)) {
      found.value = [t("loyalty.rewards.errors.decimals")];
    } else if (Number(value) <= 0 || (percent && Number(value) > 100)) {
      found.value = [t(percent ? "loyalty.rewards.errors.percentRange" : "loyalty.rewards.errors.amountRange")];
    } else if (!percent && Number(value) > 99999999.99) {
      found.value = [t("loyalty.rewards.errors.amountMax")];
    }
  }

  const points = String(form.points_required ?? "").trim();
  const stamps = String(form.stamps_required ?? "").trim();
  if ((points !== "" && !WHOLE.test(points)) || (stamps !== "" && !WHOLE.test(stamps))) {
    found.points_required = [t("loyalty.rewards.errors.costWhole")];
  } else if (Number(points || 0) <= 0 && Number(stamps || 0) <= 0) {
    found.points_required = [t("loyalty.rewards.errors.costRequired")];
  }

  return found;
};

/**
 * Create / edit one reward. An empty branch list means "every branch".
 * Services: a free service needs at least one (one matching line of the visit
 * is made free, staff picks which at payment); for a discount, none ticked
 * means every service; a free product has none — it names its product instead.
 */
export default function LoyaltyRewardModal({ open, reward, branches, services, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(toForm(reward));
    setError("");
    setFieldErrors({});
  }, [open, reward]);

  if (!open) return null;

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setText = (field, lang, value) => setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: value } }));
  const toggle = (field, id) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id],
    }));
  const err = (name) =>
    fieldErrors[name]?.[0] ??
    Object.entries(fieldErrors).find(([key]) => key.startsWith(`${name}.`))?.[1]?.[0];

  const isDiscount = form.type === "discount_amount" || form.type === "discount_percent";
  const isFreeService = form.type === "free_service";
  const isFreeProduct = form.type === "free_product";
  const isPercent = form.type === "discount_percent";

  // An amount is in the branch currency; name it when the branches agree on one.
  const currencies = [
    ...new Set(
      branches
        .filter((b) => form.branches.length === 0 || form.branches.includes(b.id))
        .map((b) => b.currency?.code)
        .filter(Boolean),
    ),
  ];
  const amountUnit = currencies.length === 1 ? currencies[0] : "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const found = clientErrors(form, t);
    if (Object.keys(found).length > 0) {
      setFieldErrors(found);
      return;
    }

    setSaving(true);
    setFieldErrors({});

    const payload = {
      name: form.name,
      description: form.description.en || form.description.ar ? form.description : null,
      type: form.type,
      value: isDiscount && form.value !== "" ? form.value : null,
      product_id: isFreeProduct ? (form.product?.id ?? null) : null,
      points_required: Number(form.points_required || 0),
      stamps_required: Number(form.stamps_required || 0),
      validity_days: intOrNull(form.validity_days),
      redemption_limit_per_client: intOrNull(form.redemption_limit_per_client),
      total_redemption_limit: intOrNull(form.total_redemption_limit),
      is_active: form.is_active,
      branches: form.branches,
      services: isFreeProduct ? [] : form.services,
    };

    try {
      const saved = reward ? await updateReward(reward.id, payload) : await createReward(payload);
      onSaved(saved, Boolean(reward));
      onClose();
    } catch (er) {
      setFieldErrors(apiFieldErrors(er));
      setError(apiErrorMessage(er, t("loyalty.rewards.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const numberInput = (name, label, hint) => (
    <div>
      <label className="field-label" htmlFor={`rw-${name}`}>
        {label}
      </label>
      <input
        id={`rw-${name}`}
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        dir="ltr"
        className={`field-input ${err(name) ? "border-danger" : ""}`}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
      />
      {hint && <p className="mt-1 text-xs text-ink-2">{hint}</p>}
      {err(name) && <p className="field-error">{err(name)}</p>}
    </div>
  );

  const checklist = (field, items, label, { required = false, hint } = {}) => (
    <fieldset>
      <legend className="field-label">
        {label} {required && <span className="text-danger-text">*</span>}
      </legend>
      <p className="mb-1 text-xs text-ink-2">{hint ?? t("loyalty.rewards.emptyMeansAll")}</p>
      <div
        className={`max-h-36 space-y-1 overflow-y-auto rounded-lg border p-2 ${
          err(field) ? "border-danger" : "border-line"
        }`}
      >
        {items.length === 0 ? (
          <p className="text-xs text-ink-2">{t("common.none")}</p>
        ) : (
          items.map((item) => (
            <label key={item.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-[var(--pl-accent)]"
                checked={form[field].includes(item.id)}
                onChange={() => toggle(field, item.id)}
              />
              {item.name}
            </label>
          ))
        )}
      </div>
      {err(field) && <p className="field-error">{err(field)}</p>}
    </fieldset>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rw-title"
        className="card my-8 w-full max-w-2xl p-5"
      >
        <h2 id="rw-title" className="mb-4 text-lg font-bold">
          {reward ? t("loyalty.rewards.editTitle") : t("loyalty.rewards.newTitle")}
        </h2>

        {error && (
          <div
            className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
            role="alert"
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            {["en", "ar"].map((lang) => (
              <div key={lang}>
                <label className="field-label" htmlFor={`rw-name-${lang}`}>
                  {t(`loyalty.rewards.name_${lang}`)} <span className="text-danger-text">*</span>
                </label>
                <input
                  id={`rw-name-${lang}`}
                  dir={lang === "ar" ? "rtl" : "ltr"}
                  className={`field-input ${err(`name.${lang}`) ? "border-danger" : ""}`}
                  value={form.name[lang] ?? ""}
                  onChange={(e) => setText("name", lang, e.target.value)}
                  maxLength={120}
                />
                {err(`name.${lang}`) && <p className="field-error">{err(`name.${lang}`)}</p>}
              </div>
            ))}
            {["en", "ar"].map((lang) => (
              <div key={`d-${lang}`}>
                <label className="field-label" htmlFor={`rw-desc-${lang}`}>
                  {t(`loyalty.rewards.description_${lang}`)}
                </label>
                <textarea
                  id={`rw-desc-${lang}`}
                  rows={2}
                  dir={lang === "ar" ? "rtl" : "ltr"}
                  className="field-input"
                  value={form.description[lang] ?? ""}
                  onChange={(e) => setText("description", lang, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="rw-type">
                {t("loyalty.rewards.type")}
              </label>
              <select
                id="rw-type"
                className="field-input"
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
              >
                {REWARD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {labelFor("reward_type", type)}
                  </option>
                ))}
              </select>
            </div>

            {isDiscount && (
              <div>
                <label className="field-label" htmlFor="rw-value">
                  {t(isPercent ? "loyalty.rewards.valuePercent" : "loyalty.rewards.valueAmount")}{" "}
                  <span className="text-danger-text">*</span>
                </label>
                {/* "%" for a percent, the currency for an amount — LTR in both languages. */}
                <div className="relative" dir="ltr">
                  <input
                    id="rw-value"
                    type="number"
                    required
                    min="0.01"
                    max={isPercent ? "100" : "99999999.99"}
                    step="0.01"
                    inputMode="decimal"
                    className={`field-input pe-14 ${err("value") ? "border-danger" : ""}`}
                    value={form.value}
                    onChange={(e) => set("value", e.target.value)}
                  />
                  {(isPercent || amountUnit) && (
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-ink-2">
                      {isPercent ? "%" : amountUnit}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-ink-2">
                  {t(isPercent ? "loyalty.rewards.valuePercentHint" : "loyalty.rewards.valueAmountHint")}
                </p>
                {err("value") && <p className="field-error">{err("value")}</p>}
              </div>
            )}
          </div>

          {isFreeProduct && (
            <RewardProductPicker
              value={form.product}
              onChange={(product) => set("product", product)}
              error={err("product_id")}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            {numberInput("points_required", t("loyalty.rewards.pointsRequired"))}
            {numberInput(
              "stamps_required",
              t("loyalty.rewards.stampsRequired"),
              t("loyalty.rewards.costHint")
            )}
            {numberInput(
              "validity_days",
              t("loyalty.rewards.validityDays"),
              t("loyalty.rewards.validityHint")
            )}
            {numberInput(
              "redemption_limit_per_client",
              t("loyalty.rewards.limitPerClient"),
              t("loyalty.rewards.noLimit")
            )}
            {numberInput(
              "total_redemption_limit",
              t("loyalty.rewards.totalLimit"),
              t("loyalty.rewards.noLimit")
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {checklist("branches", branches, t("loyalty.rewards.branches"))}
            {/* A free product is not tied to services. */}
            {!isFreeProduct &&
              checklist("services", services, t("loyalty.rewards.services"), {
                required: isFreeService,
                hint: isFreeService ? t("loyalty.rewards.freeServiceHint") : undefined,
              })}
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="accent-[var(--pl-accent)]"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
            />
            {t("loyalty.rewards.active")}
          </label>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
